import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { useAuthStore, useThemeStore } from '@/lib/store';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Bell, Languages, ShieldCheck, LogOut, ChevronRight, Lock, Moon, Sun, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { StorageWidget } from '@/components/StorageWidget';

export default function ProfilePage() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { theme, setTheme } = useThemeStore();

  useEffect(() => {
    if (theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [theme]);

  const onLogout = async () => {
    await logout();
    toast.success('Signed out');
    navigate('/login', { replace: true });
  };

  return (
    <div data-testid="profile-page">
      <TopBar title="Profile" subtitle="You and your preferences" user={user} />

      <Card className="rounded-2xl p-4 flex items-center gap-3 mb-4">
        <Avatar className="h-14 w-14 ring-2 ring-[hsl(var(--primary))]/40 shadow" data-testid="profile-avatar">
          <AvatarImage src={user?.picture} />
          <AvatarFallback className="gradient-fab text-white font-serif text-xl">{(user?.name || 'M').slice(0,1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-base truncate" data-testid="profile-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate" data-testid="profile-email">{user?.email}</div>
          {user?.is_admin && (
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-[hsl(var(--primary))]"><ShieldCheck className="h-3 w-3" /> Memoir Admin</div>
          )}
        </div>
        {user?.is_admin && (
          <Button onClick={() => navigate('/admin')} data-testid="profile-admin-link" variant="outline" size="sm" className="rounded-xl"><ShieldAlert className="h-4 w-4 mr-1" />Admin</Button>
        )}
      </Card>

      {/* Storage widget */}
      <div className="mb-4">
        <StorageWidget scope="me" testid="profile-storage-widget" />
      </div>

      <Card className="rounded-2xl divide-y">
        <Row icon={theme === 'dark' ? Moon : Sun} label={`Theme: ${theme === 'dark' ? 'Dark' : 'Light'}`} testid="row-theme">
          <Switch checked={theme === 'dark'} onCheckedChange={(v) => setTheme(v ? 'dark' : 'light')} data-testid="profile-theme-toggle" />
        </Row>
        <Row icon={Bell} label="Notifications" testid="row-notifications">
          <Button variant="ghost" size="sm" onClick={() => navigate('/notifications')} data-testid="profile-notifications-button" className="rounded-xl">Open <ChevronRight className="h-4 w-4" /></Button>
        </Row>
        <Row icon={Languages} label="Language" testid="row-language">
          <span className="text-sm text-muted-foreground">English</span>
        </Row>
        <Row icon={Lock} label="Privacy" testid="row-privacy">
          <span className="text-sm text-muted-foreground">Private by default</span>
        </Row>
        {user?.is_admin && (
          <Row icon={ShieldAlert} label="Admin panel" testid="row-admin">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="profile-admin-button" className="rounded-xl">Open <ChevronRight className="h-4 w-4" /></Button>
          </Row>
        )}
      </Card>

      <div className="mt-6">
        <Button onClick={onLogout} variant="destructive" data-testid="profile-logout-button" className="w-full rounded-xl h-12">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
      <p className="mt-6 text-center text-[11px] text-muted-foreground">Memoir™ · v1.0 · Capture. Share. Remember.</p>
    </div>
  );
}

const Row = ({ icon: Icon, label, children, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between gap-3 p-4">
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center"><Icon className="h-4 w-4" /></div>
      <div className="text-sm font-medium">{label}</div>
    </div>
    <div>{children}</div>
  </div>
);
